import { useEffect, useRef, useState } from "react";
import { AlertCircle, Home, Loader2, LogOut, RefreshCw } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { client } from "@/api/api";
import { resolveReadyWorkspaceURL } from "./workspace-status";

type Status =
  | "loading"
  | "starting"
  | "running"
  | "failed"
  | "license-expired"
  | "stopped"
  | "unknown";

type LicenseStatusResponse = {
  status?: "active" | "expired" | "starting" | "unavailable";
  expires_at?: string;
};

function formatExpiry(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);
}

function clearLicenseCookies() {
  document.cookie = "foxia_license_expired=; Max-Age=0; path=/";
  document.cookie = "foxia_license_unavailable=; Max-Age=0; path=/";
}

export default function WorkspaceStatus() {
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const formattedExpiry = formatExpiry(expiresAt);

  const checkStatus = async (signal?: AbortSignal, forceLicenseCheck = false) => {
    try {
      try {
        const licenseResponse = await client.get<LicenseStatusResponse>(
          "/gateway/me/license-status",
          {
            signal,
            params: forceLicenseCheck ? { force: "true" } : undefined,
          },
        );
        if (!mountedRef.current) return;

        const licenseStatus = licenseResponse.data;
        setExpiresAt(licenseStatus.expires_at ?? null);
        if (licenseStatus.status === "expired") {
          setStatus("license-expired");
          setError(null);
          return;
        }
      } catch (err: unknown) {
        const responseError = err as {
          response?: { data?: { code?: string } };
        };
        if (responseError.response?.data?.code !== "LICENSE_STATUS_UNAVAILABLE") {
          throw err;
        }
      }
      clearLicenseCookies();

      const res = await client.get("/gateway/me/provisioning-status", { signal });
      if (!mountedRef.current) return;
      // Assuming endpoint returns workspace object with status
      // We might need to adjust endpoint based on backend logic
      // But gateway usually Auto-Provisions on access if missing.
      // Let's use the list endpoint or specific ID if known.
      // But simpler: just retry the page reload?
      // Actually, we need to know WHEN to reload.

      // Let's assume we can get the specific workspace status.
      // If we are here, it means backend proxied us to frontend because Odoo wasn't ready.
      // So we should poll until it IS ready.

      const ws = res.data;
      if (ws.status === "Running" || ws.status === "running") {
        setStatus("running");
        window.location.replace(resolveReadyWorkspaceURL(ws.url));
      } else if (ws.status === "Failed" || ws.status === "failed") {
        setStatus("failed");
      } else if (ws.status === "needs_onboarding") {
        if (ws.user_email === "__superuser__") {
          setStatus("starting");
          return;
        }
        window.location.href = "/onboarding";
      } else {
        setStatus("starting");
      }
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      // If 404, it might mean not provisioned yet (but Gateway should provision)
      // If 500, might be transient
      console.error("Failed to check status", err);
      const responseError = err as {
        response?: { data?: { error?: string; code?: string } };
      };
      setError(responseError.response?.data?.error || "Failed to check status");
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    const controller = new AbortController();
    const initialCheck = window.setTimeout(() => checkStatus(controller.signal), 0);

    // Poll every 5 seconds
    const interval = window.setInterval(() => checkStatus(controller.signal), 5000);
    return () => {
      mountedRef.current = false;
      controller.abort();
      window.clearTimeout(initialCheck);
      window.clearInterval(interval);
    };
  }, []);

  if (status === "running") {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold">Cửa hàng đã sẵn sàng</h2>
          <p className="text-gray-500">Đang chuyển hướng tới Foxia CRM...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center bg-zinc-50/30 p-4">
      <Card className="w-full max-w-md border-none shadow-2xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-50">
            {status === "failed" ||
            status === "license-expired" ? (
              <AlertCircle className="h-8 w-8 text-red-500" />
            ) : (
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            )}
          </div>
          <CardTitle>
            {status === "license-expired"
              ? "Doanh nghiệp của bạn đã hết hạn sử dụng phần mềm"
                : status === "failed"
              ? "Thiết lập thất bại"
              : "Đang chuẩn bị không gian làm việc"}
          </CardTitle>
          <CardDescription>
            {status === "license-expired"
              ? "Workspace hiện không thể truy cập vì bản quyền phần mềm đã hết hạn."
                : status === "failed"
              ? "Đã có lỗi xảy ra khi khởi động môi trường làm việc của bạn."
              : "Hệ thống Foxia CRM của bạn đang được khởi tạo. Quá trình này có thể mất một vài phút."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === "license-expired" && (
            <>
              <div className="rounded-md border border-orange-100 bg-orange-50 p-3 text-sm text-orange-800">
                Vui lòng liên hệ quản trị viên hoặc bộ phận hỗ trợ để gia hạn quyền sử dụng phần mềm.
              </div>
              {formattedExpiry && (
                <div className="rounded-md border border-orange-100 bg-orange-50 p-3 text-sm text-orange-800">
                  License hết hạn vào {formattedExpiry}.
                </div>
              )}
            </>
          )}
          {error && (
            <div className="p-3 text-sm text-red-500 bg-red-50 rounded-md border border-red-100">
              {error}
            </div>
          )}

          <div className="text-center text-sm text-zinc-500">
            {status === "starting" &&
              "Trạng thái: Đang khởi tạo / Đang khởi động..."}
            {status === "failed" && "Hãy thử lại hoặc liên hệ hỗ trợ."}
          </div>

          <Button
            variant="outline"
            className="w-full h-11 border-zinc-200 hover:bg-zinc-50"
            onClick={() => {
              if (status === "license-expired") {
                void checkStatus(undefined, true);
                return;
              }
              window.location.reload();
            }}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {status === "license-expired"
              ? "Kiểm tra lại"
              : "Thử lại ngay"}
          </Button>
          {(status === "license-expired") && (
            <Button
              variant="ghost"
              className="w-full h-11"
              onClick={() => {
                window.location.href = "/web/logout";
              }}
            >
              Đăng xuất
            </Button>
          )}
          {status !== "license-expired" && (
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="h-11 border-zinc-200 hover:bg-zinc-50"
                onClick={() => (window.location.href = "/")}
              >
                <Home className="mr-2 h-4 w-4" />
                Trang chủ
              </Button>
              <Button
                variant="outline"
                className="h-11 border-zinc-200 hover:bg-zinc-50"
                onClick={() => (window.location.href = "/web/logout")}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Đăng xuất
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
